// ============================================================
// Topbar — title, date stepper (on Calendar route), segmented view switch,
// IT/EN toggle, notifications bell, and the primary "+ Nuova prenotazione" CTA.
//
// Two responsive notes the spec is explicit about:
//   - controls (datenav, seg, lang, topbar-actions) all carry `flex: none` so
//     they NEVER shrink-and-clip; only the title truncates.
//   - the hamburger uses `.icon-btn.hamburger` to match `.icon-btn`'s
//     specificity, so its hide/show rules win deterministically.
// Both rules live in the ported global.css; we just have to use the right
// class names here.
// ============================================================
import { useRef } from 'react';
import type { DateLabel, Lang, NotificationItem } from '../data/types';
import { Icon } from '../components/Icon';
import { t } from '../i18n/t';
import type { CalView, Route } from './routes';

interface TopbarProps {
  lang: Lang;
  onToggleLang: () => void;
  route: Route;
  onOpenSidebar: () => void;
  onGotoNotifications: () => void;
  onNewReservation: () => void;
  calView: CalView;
  onCalView: (v: CalView) => void;
  title: string;
  sub: string;
  dateLabel: DateLabel;
  notifications: NotificationItem[];
  /** Giorno selezionato (YYYY-MM-DD) — valore del date picker. */
  day: string;
  onPrevDay: () => void;
  onNextDay: () => void;
  onToday: () => void;
  onPickDay: (day: string) => void;
}

export function Topbar({
  lang,
  onToggleLang,
  route,
  onOpenSidebar,
  onGotoNotifications,
  onNewReservation,
  calView,
  onCalView,
  title,
  sub,
  dateLabel,
  notifications,
  day,
  onPrevDay,
  onNextDay,
  onToday,
  onPickDay,
}: TopbarProps) {
  const D = dateLabel[lang];
  const unread = notifications.filter((n) => !n.read).length;

  // Click sull'etichetta della data → calendario nativo del browser.
  // L'input è renderizzato ma invisibile: serve solo come ancora del picker.
  const dateInputRef = useRef<HTMLInputElement>(null);
  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    try {
      el.showPicker();
    } catch {
      el.focus(); // fallback per browser senza showPicker()
    }
  };

  const calViews: { v: CalView; ic: 'calendar' | 'list' | 'building' }[] = [
    { v: 'timeline', ic: 'calendar' },
    { v: 'agenda', ic: 'list' },
    { v: 'floor', ic: 'building' },
  ];

  return (
    <header className="topbar">
      <button
        type="button"
        className="icon-btn hamburger"
        onClick={onOpenSidebar}
        aria-label="Menu"
      >
        <Icon name="menu" size={18} />
      </button>

      <div className="topbar-title">
        <h1>{title}</h1>
        <span className="topbar-sub">{sub}</span>
      </div>

      {route === 'cal' && (
        <div className="datenav" style={{ marginLeft: 18 }}>
          <button type="button" aria-label="Previous day" onClick={onPrevDay}>
            <Icon name="cleft" size={16} />
          </button>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              type="button"
              className="date-label"
              onClick={openDatePicker}
              aria-label={lang === 'it' ? 'Scegli una data' : 'Pick a date'}
              title={lang === 'it' ? 'Scegli una data' : 'Pick a date'}
            >
              <span>{D.dow.slice(0, 3)}</span>{' '}
              <span className="serif tnum">{D.d}</span>{' '}
              <span>{D.mon.slice(0, 3)}</span>
            </button>
            <input
              ref={dateInputRef}
              type="date"
              value={day}
              onChange={(e) => {
                if (e.target.value) onPickDay(e.target.value);
              }}
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: 'none',
                border: 0,
                padding: 0,
              }}
              tabIndex={-1}
              aria-hidden="true"
            />
          </span>
          <button type="button" aria-label="Next day" onClick={onNextDay}>
            <Icon name="cright" size={16} />
          </button>
          <button type="button" className="today-btn" onClick={onToday}>
            {t('today', lang)}
          </button>
        </div>
      )}

      <div className="topbar-spacer" />

      <div className="topbar-actions">
        {route === 'cal' && (
          <div className="seg" role="tablist" aria-label="Calendar view">
            {calViews.map(({ v, ic }) => (
              <button
                key={v}
                type="button"
                className={calView === v ? 'on' : ''}
                aria-pressed={calView === v}
                onClick={() => onCalView(v)}
              >
                <Icon name={ic} size={14} />
                <span className="seglabel">{t(v, lang)}</span>
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          className="lang"
          onClick={onToggleLang}
          aria-label="Cambia lingua / Switch language"
        >
          <span className={lang === 'it' ? 'on' : ''}>IT</span>·
          <span className={lang === 'en' ? 'on' : ''}>EN</span>
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onGotoNotifications}
          aria-label={t('notifications', lang)}
        >
          <Icon name="bell" size={17} />
          {unread > 0 && <span className="notif-dot" aria-hidden="true" />}
        </button>
        <button type="button" className="btn btn-primary" onClick={onNewReservation}>
          <Icon name="plus" size={15} />
          <span className="newlabel">{t('newResv', lang)}</span>
        </button>
      </div>
    </header>
  );
}
